import { buyerApi, sellerApi } from "./AxiosConfig";

const normalizeInvoiceStatus = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

export const isOrderInvoiceAvailable = (order) => {
  if (!order) return false;

  if (normalizeInvoiceStatus(order.zoho_invoice_status) === "failed") {
    return false;
  }

  if (typeof order.zoho_invoice_available === "boolean") {
    return order.zoho_invoice_available;
  }

  if (Number(order.zoho_invoice_available) === 1) {
    return true;
  }

  return Boolean(String(order.zoho_invoice_number || "").trim());
};

export const isSellerOrderInvoiceDownloadAvailable = (order) => {
  if (!order) return false;

  if (typeof order.seller_invoice_download_available === "boolean") {
    return order.seller_invoice_download_available;
  }

  return Number(order.seller_invoice_download_available) === 1;
};

export const getOrderInvoiceFallbackFilename = (order) => {
  const invoiceNumber = String(order?.zoho_invoice_number || "").trim();
  if (invoiceNumber) {
    return `${invoiceNumber}.pdf`;
  }

  const orderNumber = String(order?.order_number || order?.id || "").trim();
  if (orderNumber) {
    return `${orderNumber}-invoice.pdf`;
  }

  const fallbackOrderId = Number(order?.order_id || order?.id || 0);
  return `invoice-order-${fallbackOrderId || "order"}.pdf`;
};

const resolveInvoiceFilename = (contentDisposition, fallbackFilename) => {
  const normalizedHeader = String(contentDisposition || "");
  const filenameStarMatch = normalizedHeader.match(/filename\*=UTF-8''([^;]+)/i);
  const filenameMatch = normalizedHeader.match(/filename="?([^"]+)"?/i);

  if (filenameStarMatch?.[1]) {
    try {
      return decodeURIComponent(filenameStarMatch[1]);
    } catch {
      return filenameStarMatch[1];
    }
  }

  if (filenameMatch?.[1]) {
    return filenameMatch[1];
  }

  return fallbackFilename;
};

export const getInvoiceDownloadErrorMessage = async (error) => {
  const fallback = "Unable to download the invoice.";
  const responseData = error?.response?.data;

  if (typeof Blob !== "undefined" && responseData instanceof Blob) {
    try {
      const text = await responseData.text();
      const parsed = JSON.parse(text);
      return parsed?.message || fallback;
    } catch {
      return fallback;
    }
  }

  return responseData?.message || error?.message || fallback;
};

export const downloadOrderInvoice = async (order) => {
  const orderId = Number(order?.order_id || order?.id || 0);

  if (!orderId) {
    throw new Error("Order ID is required");
  }

  const response = await buyerApi.get(`/orders/${orderId}/invoice/download`, {
    responseType: "blob",
  });

  const fallbackFilename = getOrderInvoiceFallbackFilename(order);
  const filename = resolveInvoiceFilename(
    response.headers?.["content-disposition"],
    fallbackFilename,
  );
  const pdfBlob = new Blob([response.data], {
    type: "application/pdf",
  });
  const objectUrl = window.URL.createObjectURL(pdfBlob);
  const downloadLink = document.createElement("a");

  downloadLink.href = objectUrl;
  downloadLink.download = filename;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  downloadLink.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
};

export const downloadSellerOrderInvoice = async (order) => {
  const orderId = Number(order?.order_id || order?.id || 0);

  if (!orderId) {
    throw new Error("Order ID is required");
  }

  const response = await sellerApi.get(
    `/seller/orders/${orderId}/invoice/download`,
    {
      responseType: "blob",
    },
  );

  const fallbackFilename = getOrderInvoiceFallbackFilename(order);
  const filename = resolveInvoiceFilename(
    response.headers?.["content-disposition"],
    fallbackFilename,
  );
  const pdfBlob = new Blob([response.data], {
    type: "application/pdf",
  });
  const objectUrl = window.URL.createObjectURL(pdfBlob);
  const downloadLink = document.createElement("a");

  downloadLink.href = objectUrl;
  downloadLink.download = filename;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  downloadLink.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
};
